import dateUtils from "@plannel/utils/date-util";
import { isEqual } from "lodash";
import { blue, lightBlue, red } from "@mui/material/colors";
import codeUtil from "@plannel/pages/utils/code-util";
 

const  { dpVersion: versionCode, dpMeasure: measureCode, customer, item, buckets: bucketCode, workbenchAlertObjs: { TARGET_MEASURE, REFER_MEASURE, UPPER_THRESHOLD, LOWER_THRESHOLD } } = codeUtil;
const editableColor = lightBlue[100];
const { MONTH, QUETER } = bucketCode;
const { PMP } = codeUtil.dpMeasure;
const defaultTimeDimension = MONTH;

const isAllEditable = (selectVersion, selectTD) => {
  return isEqual(selectTD,  selectVersion?.planConfigObj?.BUCKET_UNIT);
}

const conditionEditable = (editableParams) => {
  const { rowData, selectVersion, editableMeasure, selectTD, selectCL, selectIL } = editableParams;
  const measureCd = rowData?.resultRow?.measureCd;

  if (!isAllEditable(selectVersion, selectTD)) {
    return false;
  }

  // promotion price measure
  if (isEqual(measureCd, PMP.cd) && !(isEqual(selectCL, customer.cd) && isEqual(selectIL, item.cd))) {
    return false;
  }

  return editableMeasure.includes(measureCd) && !(isEqual(selectVersion?.statusCd, versionCode.RELEASED) || isEqual(selectVersion?.statusCd, versionCode.REFERENCE) || isEqual(selectVersion?.moduleCd, versionCode.DEFAULT));
};

const getEditDateByDtf = (dtf, startDate, unit) => {
  let date = new ZDate(startDate);
  if (dtf === null || startDate === null || unit === null) {
    return null;
  }
  if (codeUtil.isWeekBucket(unit)) {
    date = date.setDate(date.getDate() + (dtf * 7));
  } else if (isEqual(unit, MONTH)) {
    date.setDate(1);
    date = date.setMonth(date.getMonth() + dtf);
  } else if (isEqual(unit, QUETER)) {
    date.setDate(1);
    date = date.setMonth(date.getMonth() + (dtf * 4));
  } else {
    date.setDate(1);
    date = date.setFullYear(date.getFullYear() + dtf);
  }
  
  return new ZDate(date);
}

const getNextBucketDate = (selectTD, baseDate) => {
  const nextBucketDate = new ZDate(baseDate);
  if (selectTD === codeUtil.buckets.WEEK) {
    nextBucketDate.setDate(baseDate.getDate() + 7);
  } else if (selectTD === codeUtil.buckets.PARTIAL_WEEK) {
    nextBucketDate.setDate(baseDate.getDate() + 7);
    if (nextBucketDate.getMonth() !== baseDate.getMonth()) {
      nextBucketDate.setDate(1);
    }
  } else if (selectTD === codeUtil.buckets.MONTH) {
    nextBucketDate.setMonth(baseDate.getMonth() + 1);
  } else if (selectTD === codeUtil.buckets.QUARTER) {
    nextBucketDate.setMonth(baseDate.getMonth() +3);
  } else {
    nextBucketDate.setFullYear(baseDate.getFullYear() +1);
  }
  return nextBucketDate;
}

const checkEditable = (cellData, editableParams, editableStartDate, todayEditable, header = defaultTimeDimension) => {
  if (!cellData || !cellData.editableCustomerHrchy) {
    return false;
  }
  editableParams.rowData = cellData;
  const { selectVersion, selectTD } = editableParams;
  if (conditionEditable(editableParams)) {
    let isPeriod = true;
    const isEditable = cellData.editableRow && todayEditable;
    let headerDate = new ZDate(header);
    headerDate = new ZDate(headerDate.getFullYear(), headerDate.getMonth(), headerDate.getDate(), 0, 0, 0);
    headerDate = dateUtils.headerDateFormatter(headerDate, selectTD);

    const readyToSellinDt = dateUtils.headerDateFormatter(cellData.readyToSellinDt, selectTD);
    let isReadyToSellinDt = readyToSellinDt <= headerDate;
    if (!isReadyToSellinDt) {
      const nextBucketDate = getNextBucketDate(selectTD, new ZDate(headerDate));
      if (dateUtils.headerDateFormatter(nextBucketDate, selectTD) > readyToSellinDt) {
        isReadyToSellinDt = true;
      }
    }
    
    const isEndOfSalesDt = dateUtils.headerDateFormatter(cellData.endOfSalesDt, selectTD) >= headerDate;
    const isTd = dateUtils.headerDateFormatter(editableStartDate, selectTD) <= headerDate;

    if (selectVersion?.planConfigObj) {
      const { PLAN_START_DATE, BUCKET_UNIT, PLANNING_RANGE, START_DAY } = selectVersion.planConfigObj;
      const planEndDate = dateUtils.headerDateFormatter(dateUtils.calculateRangeDate(new ZDate(PLAN_START_DATE), BUCKET_UNIT, START_DAY, PLANNING_RANGE, true), selectTD);
      isPeriod = headerDate <= planEndDate;
    }

    if (cellData.readyToSellinDt !== null && cellData.endOfSalesDt !== null) {
      return isEditable && isReadyToSellinDt && isEndOfSalesDt && isTd && isPeriod;
    } else if (cellData.readyToSellinDt === null && cellData.endOfSalesDt !== null) {
      return isEditable && isEndOfSalesDt && isTd && isPeriod;
    } else if (cellData.readyToSellinDt !== null && cellData.endOfSalesDt === null) {
      return isEditable && isReadyToSellinDt && isTd && isPeriod;
    }
    return isEditable && isTd && isPeriod;
  }

  return false;
};

const getAlertBackground = (data, header) => {
  if (!data.node.group) {
    const { workbenchThreshold, dataMap } = data.context;
    if (!data?.data?.resultRow || !data?.data?.resultRow?.measure) {
      return false;
    }
    const { customerHrchyCd: cellCustomerHCd, itemHrchyCd: cellItemHCd, measure: { measureCd: cellMeasure } } = data.data.resultRow;
    
    if (dataMap) {
      if (isEqual(cellMeasure, workbenchThreshold?.[TARGET_MEASURE])) {
        const refMeasure = workbenchThreshold?.[REFER_MEASURE];
        
        if (refMeasure) {
            const refKey = `${cellCustomerHCd}${cellItemHCd}${refMeasure}${header}`;
            const upperThreshold = workbenchThreshold?.[UPPER_THRESHOLD] ?? 0;
            const lowerThreshold = workbenchThreshold?.[LOWER_THRESHOLD] ?? 0;

            const refVal = dataMap?.[refKey] ?? 0;
            const targetVal = Number(data.value);
            if (upperThreshold) {
              const upperVal = Number(refVal) * (1 + (upperThreshold / 100));
              if (upperVal < targetVal) {
                return true;
              }
            }
            if (lowerThreshold) {
              const lowerVal = Number(refVal) * (1 + (lowerThreshold / 100));
              if (lowerVal > targetVal) {
                return true;
              }
            }
        }
      }
    }
  }
  return false;
}

const dataCellStyle = (data, header, reverseHeader, editableStartDate, todayEditable, editableParams, timeDimension, index) => { // , dataMap, workbenchThreshold
  const cellStyle = {};
  if (!data.node.group) {
    const { dataMap } = data.context;
    const {
      customerHrchyCd: cellCustomerHCd,
      itemHrchyCd: cellItemHCd,
      measure: { measureCd: cellMeasure },
    } = data.data.resultRow;

    const key = `${cellCustomerHCd}${cellItemHCd}${cellMeasure}${data.colDef.field}`;
    if (dataMap) {
      const originalValue = Number(dataMap[key]);
      const currentValue = Number(data.value);
      if (!isNaN(originalValue) && originalValue !== currentValue && !isEqual(cellMeasure, measureCode.BF_AMT.cd)) {
        cellStyle.color = blue.A700;
        cellStyle.fontWeight = "bold";
      }

      if (checkEditable(data.data, editableParams, editableStartDate, todayEditable, codeUtil.isWeekBucket(timeDimension) ? reverseHeader[header] : header)) {
        cellStyle.backgroundColor = editableColor;
      }

      if (getAlertBackground(data, header)) {
        cellStyle.backgroundColor = red[100];
      }
    } 
  }

  if (data.data?.resultRow?.noteFlg && data.data.resultRow.noteFlg?.length > 0) {
    if (data.data.resultRow.noteFlg[index] !== null && data.data.resultRow.noteFlg[index]) {
      cellStyle.borderLeft = "3px solid #4caf50";
    }
  }
  return cellStyle;
};


const setNoteColor = (data) => {
  const resultRow = data?.data?.resultRow;
  if (resultRow) {
    const date = data?.column?.colId;
    const index = resultRow?.headers.indexOf(date) >= 0 ? resultRow?.headers.indexOf(date) : data?.context?.headerList.indexOf(date);
    if (resultRow?.noteFlg?.[index] !== null && resultRow?.noteFlg?.[index]) {
      return true;
    }
  }
  return false;
}

const WorkbenchEditable = {
  conditionEditable,
  getAlertBackground,
  isAllEditable,
  checkEditable,
  dataCellStyle,
  setNoteColor,
  getEditDateByDtf
}

export default WorkbenchEditable;